import _ from 'lodash';

import React, {
    useCallback,
    useContext
} from 'react';

import {
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import {
    useTranslation
} from '../../../../i18n.js';

import LandscapeContext from '../../../contexts/landscapeContext.js';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';
import Panel from '../../../components/Panel.jsx';
import Label from '../../../components/Label.jsx';
import Donut from '../../../components/charts/Donut.jsx';

import {
    useComputedCurationConfigValue,
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

const GENDER_COLORS = {
    male: 'rgb(102, 182, 227)',
    female: 'rgb(212, 103, 155)',
    other: 'rgb(249, 226, 125)'
};

function NoDataDuringPeriod() {
    return (
        <div className='tlw-flex tlw-h-[180px] tlw-items-center tlw-justify-center tlw-text-lg tlw-font-light'>No data in this time period</div>
    );
}

export default function AudienceGender({instanceId, pdfExportConfig, appSpecificExportConfig}) {

    const landscape = useContext(LandscapeContext);

    const {
        t
    } = useTranslation('apps/insights/components/AudienceGender');

    const timezone = useCurationConfigValue(instanceId, 'timezone');
    const companyId = useCurationConfigValue(instanceId, 'companyId');
    const dateRanges = useComputedCurationConfigValue(instanceId, 'dateRanges');

    const title = t('title');

    useSetExportConfig(instanceId, {
        title,
        supportedExportFormats: _.without(_.values(RivalIQ.enums.exportFormat), RivalIQ.enums.exportFormat.CSV),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        pdfExportConfig,
        appSpecificExportConfig: appSpecificExportConfig
    });

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title,
            subtitle: appSpecificExportConfig.timePeriodStr
        }
    });

    const getColor = useCallback((datum, idx) => {
        return GENDER_COLORS[datum.id];
    }, []);

    const metricRequests = {
        audienceGenders: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_PRESENCE_DATA,
            dimensions: ['companyId'],
            measure: 'audience_genders',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            },
            dimensionReductions: [{
                day: 'latestNonNull'
            }]
        }
    };

    return (
        <Panel
            instanceId={instanceId}
            title={title}
            exportSubtitle={appSpecificExportConfig.timePeriodStr}
            description={t('description')}
            showViewMore={false}
        >
            <MetricLoadingView
                instanceId={instanceId}
                metricRequests={metricRequests}>
                {
                    ({audienceGenders}) => {
                        let main = <NoDataDuringPeriod />;
                        const data = audienceGenders?.data?.[0]?.[1];
                        if (data !== null) {
                            const formatter = new Intl.NumberFormat('en-US', {
                                style: 'percent'
                            });

                            const chartData = _.map(data, (percentage, gender) => {
                                const id = gender.toLowerCase();
                                return {
                                    id: id,
                                    name: gender,
                                    formatted: formatter.format(percentage),
                                    value: percentage
                                };
                            });

                            chartData.sort((a, b) => b.value - a.value);

                            main = (
                                <Donut
                                    colors={getColor}
                                    data={chartData}
                                    legendDataKey='formatted'
                                />
                            );
                        }

                        return (
                            <article className='tlw-px-7.5 tlw-py-5'>
                                <header>
                                    <Label>{t('label')}</Label>
                                </header>
                                <main className='tlw-relative'>
                                    {main}
                                </main>
                            </article>
                        );
                    }
                }
            </MetricLoadingView>
        </Panel>
    );
}
